import {
    Archive,
    Edit3,
    FolderInput,
    FolderOutput,
    Pin,
    PinOff,
    Trash2,
    Unlink,
    type LucideIcon,
} from "lucide-react";

export type ChatActionId =
    "rename" | "moveToProject" | "detachScene" | "detachProject" | "pin" | "unpin" | "archive" | "delete";

export interface ChatActionItem {
    id: ChatActionId;
    label: string;
    icon: LucideIcon;
    onSelect?: () => void;
    disabled?: boolean;
    danger?: boolean;
}

/** Узкая translate-сигнатура — pure-модуль не зависит от i18next-типов. */
type Translate = (key: string) => string;

/**
 * Цель действия с чатом — пара id'шников, без которых меню вообще не строится.
 * Все обработчики хука начинаются с одной и той же проверки на их наличие.
 */
export interface ChatTarget {
    chatId: string;
    workspaceId: string;
}

/** Пара id'шников чата до проверки на наличие — оба поля могут быть `null`. */
interface MaybeChatTarget {
    chatId: string | null;
    workspaceId: string | null;
}

/**
 * Гард «есть ли валидная цель» — выносит повторяющийся `!chatId || !workspaceId`
 * из шести обработчиков в одну именованную, тестируемую без React предикату.
 * Narrowing-сигнатура сужает объект до `ChatTarget` (оба поля `string`).
 */
export function hasChatTarget(target: MaybeChatTarget): target is ChatTarget {
    return target.chatId != null && target.workspaceId != null;
}

/** Аргументы move-мутации: цель + новое размещение (workspace/project/scene). */
export interface MoveChatVars extends ChatTarget {
    targetWorkspaceId: string;
    targetProjectId?: string;
    targetSceneId?: string;
}

/**
 * Detach-from-scene = остаёмся в том же workspace и project, чистим только сцену.
 * Pure-builder payload'а для move-мутации (тестируется без React).
 */
export function buildDetachSceneMove(target: ChatTarget, projectId: string | null | undefined): MoveChatVars {
    return {
        chatId: target.chatId,
        workspaceId: target.workspaceId,
        targetWorkspaceId: target.workspaceId,
        targetProjectId: projectId ?? undefined,
    };
}

/**
 * Detach-from-project = поднимаем чат на уровень workspace (project и scene сброшены).
 * Pure-builder payload'а для move-мутации.
 */
export function buildDetachProjectMove(target: ChatTarget): MoveChatVars {
    return {
        chatId: target.chatId,
        workspaceId: target.workspaceId,
        targetWorkspaceId: target.workspaceId,
    };
}

export interface ChatActionHandlers {
    onRename: () => void;
    onMove: () => void;
    onDetachScene: () => void;
    onDetachProject: () => void;
    onPin: () => void;
    onUnpin: () => void;
    onArchive: () => void;
    onDelete: () => void;
}

export interface ChatActionPending {
    pin: boolean;
    unpin: boolean;
    move: boolean;
    archive: boolean;
}

export interface BuildChatActionsParams {
    t: Translate;
    isPinned: boolean;
    hasScene: boolean;
    hasProject: boolean;
    handlers: ChatActionHandlers;
    pending: ChatActionPending;
}

/**
 * Единый источник правды для набора и порядка пунктов меню действий с чатом.
 * Чистая функция — используется из `useChatActions` (и тестируется без React).
 * Порядок: rename, moveToProject, [detachScene], [detachProject], pin/unpin,
 * archive, delete.
 */
export function buildChatActionsList({
    t,
    isPinned,
    hasScene,
    hasProject,
    handlers,
    pending,
}: BuildChatActionsParams): ChatActionItem[] {
    const pinAction: ChatActionItem = isPinned
        ? {
              id: "unpin",
              label: t("chatActions.unpin"),
              icon: PinOff,
              onSelect: handlers.onUnpin,
              disabled: pending.unpin,
          }
        : {
              id: "pin",
              label: t("chatActions.pin"),
              icon: Pin,
              onSelect: handlers.onPin,
              disabled: pending.pin,
          };

    const actions: ChatActionItem[] = [
        {
            id: "rename",
            label: t("chatActions.rename"),
            icon: Edit3,
            onSelect: handlers.onRename,
        },
        {
            id: "moveToProject",
            label: t("chatActions.moveToProject"),
            icon: FolderInput,
            onSelect: handlers.onMove,
        },
    ];

    if (hasScene) {
        actions.push({
            id: "detachScene",
            label: t("chatActions.detachScene"),
            icon: Unlink,
            onSelect: handlers.onDetachScene,
            disabled: pending.move,
        });
    }
    if (hasProject) {
        actions.push({
            id: "detachProject",
            label: t("chatActions.detachProject"),
            icon: FolderOutput,
            onSelect: handlers.onDetachProject,
            disabled: pending.move,
        });
    }

    actions.push(pinAction, {
        id: "archive",
        label: t("chatActions.archive"),
        icon: Archive,
        onSelect: handlers.onArchive,
        disabled: pending.archive,
    });
    actions.push({
        id: "delete",
        label: t("chatActions.delete"),
        icon: Trash2,
        onSelect: handlers.onDelete,
        danger: true,
    });

    return actions;
}
